import React, { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Fade } from '@mui/material';

interface RouteTransitionWrapperProps {
  children: ReactNode;
}

/**
 * RouteTransitionWrapper manages route transitions and forces proper cleanup
 * between route changes, especially when navigating between different route hierarchies.
 * This solves the issue where React Router doesn't properly unmount components
 * when transitioning from deeply nested routes to shallow routes.
 */
export const RouteTransitionWrapper: React.FC<RouteTransitionWrapperProps> = ({ children }) => {
  const location = useLocation();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayChildren, setDisplayChildren] = useState(children);

  useEffect(() => {
    // Start transition
    setIsTransitioning(true);

    // Small delay to ensure state cleanup
    const transitionTimer = setTimeout(() => {
      setDisplayChildren(children);
      setIsTransitioning(false);
    }, 50);

    return () => {
      clearTimeout(transitionTimer);
    };
  }, [location.pathname, children]);

  // Use a stable key per route to avoid double remounts during transition
  const transitionKey = location.pathname;

  return (
    <Box
      key={transitionKey}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Fade in={!isTransitioning} timeout={200}>
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
          {displayChildren}
        </Box>
      </Fade>
    </Box>
  );
};